import React, { useState } from "react";
import { Modal, Button, Row, Col, Divider, Input, Form, Select, DatePicker, Radio, RadioChangeEvent } from "antd";
import { CloseCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import "./NewAveragePriceForm.css";
import moment from "moment";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export interface NewAveragePriceFormProps {
  showForm: boolean;
  closeForm: () => void;
  metalsOptions: any[];
  postAveragePrice: (metalID: string, dateRange: [], office: string) => void;
}

const NewAveragePriceForm = (props: NewAveragePriceFormProps) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [metalSelectedOption, setMetalSelectedOption] = useState("");
  const [dateRangeValue, setDateRangeValue] = useState([null, null]) as any;
  const [dateRangeFilter, setDateRangeFilter] = useState([null, null]) as any;
  const [officeValue, setOfficeValue] = useState("pg");
  const [metalFormStatus, setMetalFormStatus] = useState("") as any;
  const [metalFormMessage, setMetalFormMessage] = useState("");
  const [dateRangeFormStatus, setDateRangeFormStatus] = useState("") as any;
  const [dateRangeFormMessage, setDateRangeFormMessage] = useState("");
  const [officeFormStatus, setOfficeFormStatus] = useState("") as any;
  const [officeFormMessage, setOfficeFormMessage] = useState("");

  const closeForm = () => {
    resetFormValues();
    props.closeForm();
  };

  const validateForm = () => {
    let validationFormStatus = true;
    if (metalSelectedOption === "") {
      validationFormStatus = false;
      setMetalFormStatus("error");
      setMetalFormMessage("Material must be selected");
    }
    if (dateRangeValue[0] === null || dateRangeValue[1] === null) {
      validationFormStatus = false;
      setDateRangeFormStatus("error");
      setDateRangeFormMessage("Date range must be selected");
    }
    if (officeValue === "") {
      validationFormStatus = false;
      setOfficeFormStatus("error");
      setOfficeFormMessage("Office must be selected");
    }
    return validationFormStatus;
  };

  const openCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const confirmCancel = () => {
    resetFormValues();
    setIsCancelModalOpen(false);
    closeForm();
  };

  const declineCancel = () => {
    setIsCancelModalOpen(false);
  };

  const resetFormValues = () => {
    setOfficeValue("pg");
    setDateRangeValue([null, null]);
    setMetalSelectedOption("");
    setMetalFormStatus("");
    setMetalFormMessage("");
    setDateRangeFormStatus("");
    setDateRangeFormMessage("");
    setOfficeFormStatus("");
    setOfficeFormMessage("");
  };

  const getSelectedMaterialValue = () => {
    if (metalSelectedOption === "") return [];
    return metalSelectedOption;
  };

  const updateMetalOption = (value: any) => {
    setMetalFormStatus("");
    setMetalFormMessage("");
    setMetalSelectedOption(value);
  };

  const updateDateRangeFilter = (dateRange: string[]) => {
    setDateRangeFormStatus("");
    setDateRangeFormMessage("");
    setDateRangeValue([dayjs(dateRange[0], "YYYY-MM-DD"), dayjs(dateRange[1], "YYYY-MM-DD")]);
    setDateRangeFilter(dateRange);
  };

  const updateOfficeValue = (e: any) => {
    setOfficeFormStatus("");
    setOfficeFormMessage("");
    setOfficeValue(e.target.value);
  };

  const calculateAveragePrice = () => {
    props.postAveragePrice(metalSelectedOption, dateRangeFilter, officeValue);
    resetFormValues();
  };

  const officeOptions = [
    {
      label: "Penang (PG)",
      value: "pg",
    },
    {
      label: "Butterworth (BW)",
      value: "bw",
    },
  ];

  return props.showForm ? (
    <Row justify="center">
      <div className="new-average-price-form">
        <Row>
          <Col span={20}>
            <Row justify="start">New Average Price</Row>
          </Col>
          <Col span={4}>
            <Row justify="end">
              <CloseCircleOutlined onClick={() => openCancelModal()} />
            </Row>
          </Col>
        </Row>
        <Row>
          <Divider style={{ margin: "12px 0" }} />
        </Row>

        <Form layout="vertical">
          <Row align="middle">
            <Col span={24}>
              <Row align="middle">
                <Form.Item
                  label={<label className="new-average-price-form-label">Select Material Name</label>}
                  validateStatus={metalFormStatus}
                  help={metalFormMessage}
                  style={{ width: "100%" }}
                >
                  <Select
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Select a material"
                    value={getSelectedMaterialValue()}
                    onChange={updateMetalOption}
                    options={props.metalsOptions}
                    filterOption={(input: any, option: any) => {
                      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  />
                </Form.Item>
              </Row>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={24}>
              <Row align="middle">
                <Form.Item
                  label={<label className="new-average-price-form-label">Select Date Range</label>}
                  validateStatus={dateRangeFormStatus}
                  help={dateRangeFormMessage}
                  style={{ width: "100%" }}
                >
                  <RangePicker
                    style={{ width: "100%" }}
                    value={dateRangeValue}
                    onChange={(value, dateString: string[]) => {
                      updateDateRangeFilter(dateString);
                    }}
                    disabledDate={(current) => {
                      let customDate = moment().add(1, "day").format("YYYY-MM-DD");
                      return current && current > moment(customDate, "YYYY-MM-DD");
                    }}
                  />
                </Form.Item>
              </Row>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={24}>
              <Row align="middle">
                <Form.Item
                  label={<label className="new-average-price-form-label">Select Office</label>}
                  validateStatus={officeFormStatus}
                  help={officeFormMessage}
                >
                  <Radio.Group
                    options={officeOptions}
                    onChange={(e: any) => updateOfficeValue(e)}
                    optionType="button"
                    buttonStyle="solid"
                    value={officeValue}
                  />
                  {/* <Radio.Group onChange={updateOfficeValue} value={officeValue}>
                    <Radio value={"pg"}>Penang</Radio>
                    <Radio value={"bw"}>Butterworth</Radio>
                  </Radio.Group> */}
                </Form.Item>
              </Row>
            </Col>
          </Row>
        </Form>
        <Row justify="center" style={{ marginTop: "20px" }}>
          <Button className="cancel-button" onClick={() => openCancelModal()}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => validateForm() && calculateAveragePrice()}>
            Calculate Average Price
          </Button>
        </Row>
      </div>
      <Modal
        title="Cancel Average Price Calculation"
        open={isCancelModalOpen}
        okText="Confirm"
        okType="danger"
        onOk={confirmCancel}
        onCancel={declineCancel}
      >
        <p>Are you sure you want to cancel?</p>
        <p>Your changes will not be saved.</p>
      </Modal>
    </Row>
  ) : null;
};

export default NewAveragePriceForm;
